import { formatDate } from '@amelio/ui-library';

import { ALL_EVALUATORS, ALL_PERIODS, CUSTOM_PERIOD } from './skillsetPeriodIds';

import type { SkillsetPeriod } from './skillsetPeriodIds';
import type { EvaluationPeriod } from '@/types/manager-assessment/Evaluation';
import type { PerformanceScoreWindow } from '@/types/performance-profile';

import { toIsoDate } from '@/utils/dateUtils';

/** How far back the two dates of the custom option start, before anyone picks them. */
const CUSTOM_OPTION_SPAN_IN_MONTHS = 3;

/**
 * The periods the picker may list, out of those the employee was evaluated over.
 *
 * Two of the picker's options are not periods but instructions -- "every period" and "custom
 * dates" -- and they carry reserved ids. A stored period answering to one of those ids would add a
 * second option under a value that already means something else, so it is left out of the
 * predefined list. It stays reachable: "every period" counts it, and "custom dates" spans it.
 */
export const selectableSkillsetPeriods = (
    evaluationPeriods: EvaluationPeriod[] | undefined,
): EvaluationPeriod[] | undefined =>
    evaluationPeriods?.filter((period) => period.id !== ALL_PERIODS && period.id !== CUSTOM_PERIOD);

/**
 * The period the profile opens on: every period, so the score and the competencies read each
 * competency's latest known rating instead of confining themselves to the cycle in progress -- an
 * employee not yet evaluated in it would otherwise open as never evaluated.
 *
 * The two dates only matter once the reader picks "custom dates"; until then they are the span the
 * picker opened with, and `toScoreWindow` leaves them out of what it asks the server.
 */
export const openingSkillsetPeriod = (today: Date = new Date()): SkillsetPeriod => {
    const spanStart = new Date(today);
    spanStart.setMonth(spanStart.getMonth() - CUSTOM_OPTION_SPAN_IN_MONTHS);

    return { filterId: ALL_PERIODS, fromDate: spanStart, toDate: today };
};

/**
 * The window the competency filters put on the performance score.
 *
 * "Every period" sends nothing at all, dates included: the two dates the picker carries while it is
 * on that option are the ones it opened with, not a span anyone asked for, and sent they would
 * quietly confine the score to the last three months. Every other option sends what it names.
 */
export const toScoreWindow = (
    period: SkillsetPeriod,
    evaluatorId: string,
): PerformanceScoreWindow => {
    const window: PerformanceScoreWindow = {};

    if (evaluatorId !== ALL_EVALUATORS) {
        window.evaluatorId = evaluatorId;
    }

    if (period.filterId === ALL_PERIODS) {
        return window;
    }

    if (period.filterId !== CUSTOM_PERIOD) {
        window.filterId = period.filterId;
    }

    return { ...window, fromDate: toIsoDate(period.fromDate), toDate: toIsoDate(period.toDate) };
};

/**
 * The words for the window above: the picked evaluation period by its name, a pair of dates by its
 * range, and `null` for "every period" -- which the score card turns into its own wording rather
 * than naming a span that does not exist.
 */
export const describeSkillsetPeriod = (
    period: SkillsetPeriod,
    evaluationPeriods: EvaluationPeriod[] | undefined,
    language: string,
): string | null => {
    if (period.filterId === ALL_PERIODS) {
        return null;
    }

    if (period.filterId === CUSTOM_PERIOD) {
        const from = formatDate({
            date: toIsoDate(period.fromDate),
            format: 'MMM D, YYYY',
            language,
        });
        const to = formatDate({ date: toIsoDate(period.toDate), format: 'MMM D, YYYY', language });
        return `${from} - ${to}`;
    }

    return evaluationPeriods?.find((candidate) => candidate.id === period.filterId)?.name ?? null;
};
